import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title as ChartTitle, Tooltip, Legend, Filler, BarElement,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { CircleMarker, GeoJSON, MapContainer, Polyline, Popup, TileLayer } from 'react-leaflet';
import HeatmapLayer from '../../components/HeatmapLayer';
import {
  MapIcon, ChartBarIcon, ClipboardDocumentListIcon,
  CircleStackIcon, NewspaperIcon, UsersIcon, Cog6ToothIcon,
  ArrowRightOnRectangleIcon, ChevronLeftIcon, ChevronRightIcon,
  PlusCircleIcon, ArrowLeftIcon, ShieldExclamationIcon,
  ScaleIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, HeartIcon, Bars3Icon, DocumentTextIcon,
  ShareIcon, KeyIcon, ClockIcon, BanknotesIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../contexts/Auth/Auth.Context';
import axios from 'axios';
import { REACT_APP_UPLOADS, REACT_APP_SESSION_DATA } from '../../constants';

import { Drawer } from '@mantine/core';
import shp from 'shpjs';
import { BASEMAPS, type BasemapKey } from '../../constants/basemaps';
import { maskName } from '../../utils/privacy';
import { splitSpecies } from '../../utils/species';
import ExcelUploader from '../../components/ExcelUploader';
import DatasetView            from './Dashboard/DatasetView';
import ReportedIncidentsView  from './Dashboard/ReportedIncidentsView';
import CasePrecedentsView     from './Dashboard/CasePrecedentsView';
import ArticlesView           from './Dashboard/ArticlesView';
import UserManagementView     from './Dashboard/UserManagementView';
import SystemHealthView       from './Dashboard/SystemHealthView';
import ExportView             from './Dashboard/ExportView';
import ProfileSettingsView    from './Dashboard/ProfileSettingsView';
import ApiKeysView              from './Dashboard/ApiKeysView';
import OffenderIntelligenceView from './Dashboard/OffenderIntelligenceView';
import NetworkAnalysisView      from './Dashboard/NetworkAnalysisView';
import SpeciesWatchView         from './Dashboard/SpeciesWatchView';
import UserReportView           from './Dashboard/UserReportView';
import SentencingIntelligenceView from './Dashboard/SentencingIntelligenceView';
import HearingDelayView         from './Dashboard/HearingDelayView';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ChartTitle, Tooltip, Filler, Legend,
);

/* ── Types ───────────────────────────────────────────────────────── */

type Role = 'guest' | 'user' | 'admin' | 'super-admin';

type View =
  | 'map'
  | 'analytics'
  | 'my-reports'
  | 'species-watch'
  | 'offender-intelligence'
  | 'network-analysis'
  | 'sentencing-intelligence'
  | 'hearing-delay'
  | 'my-report'
  | 'dataset-management'
  | 'reported-incidents'
  | 'case-precedents'
  | 'articles'
  | 'user-management'
  | 'system-health'
  | 'export'
  | 'settings'
  | 'api-keys';

function resolveRole(token: string | null | undefined, role?: string): Role {
  if (!token) return 'guest';
  const r = role?.toLowerCase();
  if (!r) return 'user';         // role not yet loaded — default to least privilege
  if (r === 'user') return 'user';
  if (r === 'admin') return 'admin';
  return 'super-admin';
}

/* ── Chart config ────────────────────────────────────────────────── */

const chartOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top' as const,
      labels: { color: '#9ca3af', font: { size: 10 }, boxWidth: 10, padding: 8 },
    },
    title: { display: false },
  },
  scales: {
    x: { ticks: { color: '#6b7280', font: { size: 9 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
    y: { ticks: { color: '#6b7280', font: { size: 9 } }, grid: { color: 'rgba(255,255,255,0.06)' } },
  },
};

const CHART_COLORS = [
  'rgba(255,99,132,0.75)', 'rgba(54,162,235,0.75)', 'rgba(255,206,86,0.75)',
  'rgba(75,192,192,0.75)', 'rgba(153,102,255,0.75)', 'rgba(255,159,64,0.75)',
  'rgba(201,203,207,0.75)',
];

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

/* ── Role badge styles ───────────────────────────────────────────── */

const ROLE_STYLE: Record<Role, { label: string; cls: string }> = {
  guest:          { label: 'Guest',       cls: 'bg-white/10 text-white/50 border-white/10' },
  user:           { label: 'User',        cls: 'bg-green-900/60 text-green-400 border-green-500/20' },
  admin:          { label: 'Admin',       cls: 'bg-amber-900/60 text-amber-400 border-amber-500/20' },
  'super-admin':  { label: 'Super Admin', cls: 'bg-sky-900/60 text-sky-400 border-sky-500/20' },
};

/* ── View titles ─────────────────────────────────────────────────── */

const VIEW_TITLE: Record<View, string> = {
  'map':                      'Map Intelligence',
  'analytics':                'Analytics',
  'my-reports':               'My Reports',
  'my-report':                'Intelligence Report',
  'species-watch':            'Species Watch',
  'offender-intelligence':    'Offender Intelligence',
  'network-analysis':         'Network Analysis',
  'sentencing-intelligence':  'Sentencing Intelligence',
  'hearing-delay':            'Case Delay Monitor',
  'dataset-management':       'Dataset Management',
  'reported-incidents':       'Reported Incidents',
  'case-precedents':          'Case Precedents',
  'articles':                 'Articles & News',
  'user-management':          'User Management',
  'system-health':            'System Health',
  'export':                   'Export Data',
  'api-keys':                 'Developer API Keys',
  'settings':                 'Profile & Settings',
};

/* ── Sidebar nav definition ──────────────────────────────────────── */

interface NavItem {
  id: View;
  label: string;
  Icon: React.ElementType;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

function buildNav(role: Role): NavSection[] {
  const intelligence: NavSection = {
    title: 'Intelligence',
    items: [
      { id: 'map',                      label: 'Map Intelligence',       Icon: MapIcon },
      { id: 'network-analysis',         label: 'Network Analysis',       Icon: ShareIcon },
      { id: 'species-watch',            label: 'Species Watch',          Icon: HeartIcon },
      { id: 'offender-intelligence',    label: 'Offender Intelligence',  Icon: ShieldExclamationIcon },
      { id: 'sentencing-intelligence',  label: 'Sentencing',             Icon: BanknotesIcon },
      { id: 'hearing-delay',            label: 'Case Delays',            Icon: ClockIcon },
    ],
  };

  if (role === 'guest') return [
    intelligence,
    {
      title: 'Data',
      items: [
        { id: 'export', label: 'Request Data', Icon: ArrowDownTrayIcon },
      ],
    },
  ];

  if (role === 'user') {
    return [
      {
        title: 'Intelligence',
        items: [
          { id: 'map',                     label: 'Map Intelligence',      Icon: MapIcon },
          { id: 'network-analysis',        label: 'Network Analysis',      Icon: ShareIcon },
          { id: 'analytics',               label: 'Analytics',             Icon: ChartBarIcon },
          { id: 'species-watch',           label: 'Species Watch',         Icon: HeartIcon },
          { id: 'offender-intelligence',   label: 'Offender Intelligence', Icon: ShieldExclamationIcon },
          { id: 'sentencing-intelligence', label: 'Sentencing',            Icon: BanknotesIcon },
          { id: 'hearing-delay',           label: 'Case Delays',           Icon: ClockIcon },
        ],
      },
      {
        title: 'Data',
        items: [
          { id: 'dataset-management', label: 'Dataset',      Icon: CircleStackIcon },
          { id: 'export',             label: 'Request Data', Icon: ArrowDownTrayIcon },
        ],
      },
      {
        title: 'My Activity',
        items: [
          { id: 'my-report', label: 'My Report',  Icon: DocumentTextIcon },
          { id: 'api-keys',  label: 'API Keys',   Icon: KeyIcon },
          { id: 'settings',  label: 'Settings',   Icon: Cog6ToothIcon },
        ],
      },
    ];
  }

  const analyticsSection: NavSection = {
    title: 'Intelligence',
    items: [
      { id: 'map',                     label: 'Map Intelligence',      Icon: MapIcon },
      { id: 'network-analysis',        label: 'Network Analysis',      Icon: ShareIcon },
      { id: 'analytics',               label: 'Analytics',             Icon: ChartBarIcon },
      { id: 'species-watch',           label: 'Species Watch',         Icon: HeartIcon },
      { id: 'offender-intelligence',   label: 'Offender Intelligence', Icon: ShieldExclamationIcon },
      { id: 'sentencing-intelligence', label: 'Sentencing',            Icon: BanknotesIcon },
      { id: 'hearing-delay',           label: 'Case Delays',           Icon: ClockIcon },
    ],
  };

  const managementSection: NavSection = {
    title: 'Management',
    items: [
      { id: 'dataset-management',  label: 'Dataset',            Icon: CircleStackIcon },
      { id: 'reported-incidents',  label: 'Reported Incidents', Icon: ClipboardDocumentListIcon },
      { id: 'case-precedents',     label: 'Case Precedents',    Icon: ScaleIcon },
      { id: 'articles',            label: 'Articles & News',    Icon: NewspaperIcon },
    ],
  };

  const systemSection: NavSection = {
    title: 'System',
    items: [
      { id: 'system-health', label: 'System Health', Icon: Cog6ToothIcon },
      { id: 'export',        label: 'Export Data',   Icon: ArrowDownTrayIcon },
      { id: 'api-keys',      label: 'API Keys',      Icon: KeyIcon },
      { id: 'settings',      label: 'Settings',      Icon: Cog6ToothIcon },
    ],
  };

  if (role === 'admin') return [analyticsSection, managementSection, systemSection];

  return [
    analyticsSection,
    managementSection,
    {
      title: 'System',
      items: [
        { id: 'user-management', label: 'User Management', Icon: UsersIcon },
        { id: 'system-health',   label: 'System Health',   Icon: Cog6ToothIcon },
        { id: 'export',          label: 'Export Data',     Icon: ArrowDownTrayIcon },
        { id: 'api-keys',        label: 'API Keys',        Icon: KeyIcon },
        { id: 'settings',        label: 'Settings',        Icon: Cog6ToothIcon },
      ],
    },
  ];
}

/* ── My Reports view ─────────────────────────────────────────────── */

function MyReportsView() {
  const { state } = useContext(AuthContext);
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${REACT_APP_UPLOADS}/api/v1/seizure-cases/my-reports`, {
        headers: { Authorization: `Bearer ${state.userToken}` },
      })
      .then((res) => { if (res.status === 200 && Array.isArray(res.data)) setReports(res.data); })
      .catch(() => { /* endpoint may not exist yet */ })
      .finally(() => setLoading(false));
  }, []);

  const statusCls = (s: string) => {
    const l = s?.toLowerCase();
    if (l === 'approved' || l === 'concluded') return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20';
    if (l === 'pending'  || l === 'ongoing')   return 'bg-violet-500/15 text-violet-400 border-violet-500/20';
    return 'bg-amber-500/15 text-amber-400 border-amber-500/20';
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-green-500/40 border-t-green-400 animate-spin" />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-10">
        <ClipboardDocumentListIcon className="w-12 h-12 text-white/15" />
        <p className="text-white/40 text-sm">You have not submitted any reports yet.</p>
        <a
          href="/report-incident"
          className="px-5 py-2.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all"
        >
          Submit Your First Report &rarr;
        </a>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>
      <div className="max-w-4xl mx-auto flex flex-col gap-3">
        {reports.map((r: any, i: number) => (
          <div
            key={i}
            className="rounded-xl border border-white/8 px-5 py-4 flex items-center justify-between gap-4"
            style={{ background: '#0a2e18' }}
          >
            <div className="flex-1 min-w-0">
              <p className="text-white/85 text-sm font-semibold truncate">{r.species || 'Unknown Species'}</p>
              <p className="text-white/35 text-xs mt-0.5 truncate">{r.location || '—'}</p>
            </div>
            <div className="text-white/30 text-xs shrink-0 hidden sm:block">
              {r.dateOfSeizure || r.createdAt || '—'}
            </div>
            <span className={`shrink-0 px-2.5 py-1 rounded-full border text-[10px] font-semibold tracking-wide ${statusCls(r.status || r.caseStatus)}`}>
              {r.status || r.caseStatus || 'Pending'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Map layer constants ─────────────────────────────────────────── */

const MIGRATION_CORRIDORS: { name: string; pts: [number, number][] }[] = [
  { name: 'Wildebeest Migration (Serengeti → Mara)', pts: [[-2.30, 34.80], [-1.80, 34.90], [-1.50, 35.15]] },
  { name: 'Tsavo–Amboseli Elephant Corridor',        pts: [[-2.80, 38.50], [-3.00, 37.80], [-2.65, 37.25]] },
  { name: 'Selous–Ruaha Corridor',                   pts: [[-9.00, 37.50], [-8.00, 36.50], [-7.60, 35.00]] },
  { name: 'KAZA Elephant Corridor',                  pts: [[-19.30, 22.90], [-18.10, 24.40], [-18.50, 26.50]] },
  { name: 'Amboseli–Tsavo–Galana Corridor',          pts: [[-2.65, 37.25], [-2.40, 38.00], [-2.80, 38.50]] },
  { name: 'Laikipia–Samburu Corridor',               pts: [[0.00, 36.80], [0.30, 37.10], [0.63, 37.55]] },
];

/* ═══════════════════════════════════════════════════════════════════ */

export default function UserDashboard() {
  const { state, dispatch } = useContext(AuthContext);
  const role = resolveRole(state.userToken, state.userData?.role);

  const [sidebarOpen,      setSidebarOpen]      = useState(true);
  const [mobileSidebar,    setMobileSidebar]    = useState(false);
  const [activeView,       setActiveView]       = useState<View>('map');
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const [mapMode,          setMapMode]          = useState<'markers' | 'heatmap'>('heatmap');
  const [mapLayers,        setMapLayers]        = useState({ parks: true, routes: false, migration: false });
  const [basemap,          setBasemap]          = useState<BasemapKey>('dark');
  const [parksGeoJson,     setParksGeoJson]     = useState<any>(null);
  const [parksLoading,     setParksLoading]     = useState(false);
  const [parksError,       setParksError]       = useState(false);

  const [month,   setMonth]   = useState('all');
  const [year,    setYear]    = useState('all');
  const [species, setSpecies] = useState('all');
  const [status,  setStatus]  = useState('all');

  const [data,        setData]        = useState<any[]>([]);
  const [allData,     setAllData]     = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${REACT_APP_UPLOADS}/api/v1/seizure-cases/all-cases`)
      .then((res) => {
        if (res.status === 200) { setData(res.data); setAllData(res.data); }
      })
      .catch(() => { /* non-critical */ })
      .finally(() => setDataLoading(false));
  }, []);

  useEffect(() => {
    let f = allData;
    if (species !== 'all') f = f.filter((item: any) =>
      splitSpecies(item.species).map((s) => s.toLowerCase()).includes(species.toLowerCase())
    );
    if (status !== 'all') f = f.filter((item: any) =>
      item.caseStatus?.toLowerCase() === status.toLowerCase()
    );
    if (month !== 'all') f = f.filter((item: any) => {
      if (!item.dateOfSeizure) return false;
      return new Date(item.dateOfSeizure).toLocaleString('default', { month: 'long' }).toLowerCase() === month.toLowerCase();
    });
    if (year !== 'all') f = f.filter((item: any) => {
      if (!item.dateOfSeizure) return false;
      return new Date(item.dateOfSeizure).getFullYear().toString() === year;
    });
    setData(f);
  }, [species, status, month, year, allData]);

  /* ── Lazy-load protected areas shapefile when parks layer toggled on ── */
  useEffect(() => {
    if (!mapLayers.parks || parksGeoJson || parksLoading) return;
    setParksLoading(true);
    setParksError(false);
    shp('/gis-data/ProtectedAreas.zip')
      .then((data) => {
        // shpjs can return a FeatureCollection or an array — normalise to one FC
        if (Array.isArray(data)) {
          setParksGeoJson({
            type: 'FeatureCollection' as const,
            features: data.flatMap((fc) => fc.features ?? []),
          });
        } else {
          setParksGeoJson(data);
        }
      })
      .catch(() => setParksError(true))
      .finally(() => setParksLoading(false));
  }, [mapLayers.parks, parksGeoJson, parksLoading]);

  const statuses = useMemo(() => data.reduce(
    (acc: any, item: any) => {
      const s = item?.caseStatus?.toLowerCase();
      if (s === 'ongoing') acc.ongoing++;
      else if (s === 'concluded') acc.concluded++;
      else if (s === 'absconded') acc.absconded++;
      return acc;
    },
    { ongoing: 0, concluded: 0, absconded: 0 }
  ), [data]);

  const speciesList = useMemo(() => {
    const set = new Set<string>();
    data.forEach((item: any) => {
      splitSpecies(item.species).forEach((s) => set.add(s.toLowerCase()));
    });
    return Array.from(set);
  }, [data]);

  const speciesChartData = useMemo(() => {
    const counts = speciesList
      .map((s) => ({ s, c: data.filter((item: any) => splitSpecies(item.species).map((x) => x.toLowerCase()).includes(s)).length }))
      .sort((a, b) => b.c - a.c);
    const top6 = counts.slice(0, 6);
    const othersSum = counts.slice(6).reduce((sum, i) => sum + i.c, 0);
    return { labels: [...top6.map((i) => i.s), 'Others'], values: [...top6.map((i) => i.c), othersSum] };
  }, [speciesList, data]);

  const yearChartData = useMemo(() => {
    const yrs: Record<string, number> = {};
    data.forEach((item: any) => {
      if (item.dateOfSeizure) { const y = new Date(item.dateOfSeizure).getFullYear(); if (y > 2000) yrs[y] = (yrs[y] || 0) + 1; }
    });
    const sorted = Object.keys(yrs).sort();
    return { labels: sorted, values: sorted.map((y) => yrs[y]) };
  }, [data]);

  /* ── Judicial analytics ─────────────────────────────────────────── */
  const convictionRate = useMemo(() => {
    if (!data.length) return 0;
    return Math.round((statuses.concluded / data.length) * 100);
  }, [data, statuses]);

  const courtChartData = useMemo(() => {
    const counts: Record<string, number> = {};
    data.forEach((d: any) => { const c = d.court?.trim(); if (c) counts[c] = (counts[c] || 0) + 1; });
    const top6 = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6);
    return { labels: top6.map(([n]) => n), values: top6.map(([, v]) => v) };
  }, [data]);

  const convictionByYearData = useMemo(() => {
    const yrs: Record<string, { total: number; concluded: number }> = {};
    data.forEach((d: any) => {
      if (!d.dateOfSeizure) return;
      const y = new Date(d.dateOfSeizure).getFullYear();
      if (y < 2000) return;
      const key = String(y);
      if (!yrs[key]) yrs[key] = { total: 0, concluded: 0 };
      yrs[key].total++;
      if (d.caseStatus?.toLowerCase() === 'concluded') yrs[key].concluded++;
    });
    const labels = Object.keys(yrs).sort();
    return {
      labels,
      convRate: labels.map((y) => (yrs[y].total > 0 ? Math.round((yrs[y].concluded / yrs[y].total) * 100) : 0)),
    };
  }, [data]);

  const heatPoints = useMemo(
    () =>
      data
        .filter((d: any) => d.latitude != null && d.longitude != null)
        .map((d: any): [number, number, number] => [
          Number(d.latitude),
          Number(d.longitude),
          0.6,
        ]),
    [data]
  );

  /* ── Trafficking routes: connect locations of the same offender ── */
  const trafficRoutes = useMemo(() => {
    const byOffender: Record<string, [number, number][]> = {};
    allData.forEach((d: any) => {
      const name = d.nameOfAccused?.trim();
      if (!name || d.latitude == null || d.longitude == null) return;
      const key = name.toLowerCase();
      if (!byOffender[key]) byOffender[key] = [];
      byOffender[key].push([Number(d.latitude), Number(d.longitude)]);
    });
    const routes: { name: string; pts: [number, number][] }[] = [];
    Object.entries(byOffender).forEach(([name, pts]) => {
      // Deduplicate nearby points (within ~0.01°)
      const unique: [number, number][] = [];
      pts.forEach((p) => {
        if (!unique.some((u) => Math.abs(u[0] - p[0]) < 0.01 && Math.abs(u[1] - p[1]) < 0.01)) unique.push(p);
      });
      if (unique.length >= 2) routes.push({ name, pts: unique });
    });
    return routes;
  }, [allData]);

  const natChartData = useMemo(() => {
    const counts: Record<string, number> = {};
    data.forEach((item: any) => { const n = item.nationality?.toUpperCase(); if (n) counts[n] = (counts[n] || 0) + 1; });
    const top6 = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6);
    return { labels: top6.map(([n]) => n), values: top6.map(([, c]) => c) };
  }, [data]);

  function markerFill(s: string) {
    const l = s?.toLowerCase();
    if (l === 'ongoing')   return 'rgba(77,5,232,1)';
    if (l === 'concluded') return 'rgb(15,219,32)';
    return 'rgba(248,148,6,1)';
  }

  function statusPopupStyle(s: string) {
    const l = s?.toLowerCase();
    if (l === 'ongoing')   return { background: 'rgba(77,5,232,0.15)',  color: 'rgb(167,139,250)' };
    if (l === 'concluded') return { background: 'rgba(15,219,32,0.15)', color: 'rgb(134,239,172)' };
    return { background: 'rgba(248,148,6,0.15)', color: 'rgb(251,191,36)' };
  }

  const navSections = buildNav(role);
  const selCls = 'bg-[#052e16]/80 border border-white/10 text-white text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 appearance-none cursor-pointer backdrop-blur-sm';

  function handleNavClick(id: View) {
    setActiveView(id);
    setMobileSidebar(false);
  }

  /* ── Sidebar content (shared between desktop + mobile drawer) ── */
  function SidebarContent({ compact }: { compact: boolean }) {
    return (
      <>
        {/* Logo */}
        <div className="flex items-center gap-3 px-3.5 py-4 border-b border-white/6 shrink-0">
          <img src="/ICON2.png" alt="WITS" className="w-7 h-7 shrink-0 object-contain" />
          {!compact && (
            <div className="overflow-hidden">
              <p className="text-white text-xs font-bold tracking-wider whitespace-nowrap" style={{ fontFamily: 'var(--font-display)' }}>WITS</p>
              <p className="text-green-400 text-[9px] tracking-widest uppercase whitespace-nowrap">Intelligence</p>
            </div>
          )}
        </div>

        {/* Nav sections */}
        <nav className="flex flex-col gap-0 p-2 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
          {navSections.map((section, si) => (
            <div key={si} className={si > 0 ? 'mt-2' : ''}>
              {!compact && (
                <p className="text-white/20 text-[9px] uppercase tracking-[0.15em] font-semibold px-2.5 pt-2 pb-1.5">{section.title}</p>
              )}
              {compact && si > 0 && <div className="my-2 border-t border-white/6 mx-1" />}
              {section.items.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => handleNavClick(id)}
                  title={compact ? label : undefined}
                  className={`flex items-center gap-3 px-2.5 py-2.5 rounded-lg transition-all text-left w-full ${
                    activeView === id
                      ? 'bg-green-700/25 text-green-400 border border-green-500/20'
                      : 'text-white/45 hover:bg-white/5 hover:text-white/75 border border-transparent'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {!compact && <span className="text-xs font-medium truncate">{label}</span>}
                </button>
              ))}
            </div>
          ))}

          {/* Submit report shortcut for users */}
          {role === 'user' && (
            <>
              <div className="my-2 border-t border-white/6" />
              <a
                href="/report-incident"
                title={compact ? 'Submit Report' : undefined}
                className="flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-green-400/70 hover:bg-green-500/8 hover:text-green-400 transition-all border border-transparent"
              >
                <PlusCircleIcon className="w-4 h-4 shrink-0" />
                {!compact && <span className="text-xs font-medium truncate">Submit Report</span>}
              </a>
            </>
          )}
        </nav>

        {/* User card + sign out + collapse */}
        <div className="p-2 border-t border-white/6 shrink-0 flex flex-col gap-1">
          {!compact && state.userToken && (
            <div className="px-2.5 py-2 rounded-lg mb-1" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-6 h-6 rounded-full bg-green-700 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                  {(state.userData?.name || state.userData?.username || 'U')[0].toUpperCase()}
                </div>
                <span className="text-white/65 text-xs truncate leading-tight">
                  {state.userData?.name || state.userData?.username}
                </span>
              </div>
              <span className={`text-[9px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full border ${ROLE_STYLE[role].cls}`}>
                {ROLE_STYLE[role].label}
              </span>
            </div>
          )}

          {compact ? (
            /* Collapsed: stack sign-out + expand vertically so expand is always visible */
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => setSidebarOpen(true)}
                title="Expand"
                className="p-2 rounded-lg text-white/40 hover:text-white/75 hover:bg-white/5 transition-all hidden md:flex"
              >
                <ChevronRightIcon className="w-3.5 h-3.5" />
              </button>
              {state.userToken ? (
                <button
                  onClick={() => { localStorage.removeItem(REACT_APP_SESSION_DATA); dispatch({ type: 'SIGN_OUT' } as any); window.location.href = '/'; }}
                  title="Sign out"
                  className="p-2 rounded-lg text-white/25 hover:text-red-400 hover:bg-red-500/8 transition-all"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                </button>
              ) : (
                <a href="/accounts/login" title="Sign In" className="p-2 rounded-lg text-green-400 hover:bg-green-500/10 transition-all">
                  <span className="text-xs font-bold">&rarr;</span>
                </a>
              )}
            </div>
          ) : (
            /* Expanded: sign-out on left, collapse on right */
            <div className="flex items-center justify-between gap-1">
              {state.userToken ? (
                <button
                  onClick={() => { localStorage.removeItem(REACT_APP_SESSION_DATA); dispatch({ type: 'SIGN_OUT' } as any); window.location.href = '/'; }}
                  title="Sign out"
                  className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/8 transition-all flex-1"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 shrink-0" />
                  <span className="text-xs truncate">Sign out</span>
                </button>
              ) : (
                <a
                  href="/accounts/login"
                  className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-green-400 hover:bg-green-500/10 transition-all flex-1"
                >
                  <span className="text-xs font-semibold">Sign In &rarr;</span>
                </a>
              )}
              <button
                onClick={() => setSidebarOpen(false)}
                title="Collapse"
                className="p-2 rounded-lg text-white/25 hover:text-white/55 hover:bg-white/5 transition-all shrink-0 hidden md:flex"
              >
                <ChevronLeftIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </>
    );
  }

  /* ── Map view ────────────────────────────────────────────────────── */
  const MapView = (
    <div className="flex-1 relative" style={{ minHeight: 0 }}>
      <MapContainer
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        center={[0.23, 37.52]}
        zoom={6}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        <TileLayer
          key={basemap}
          url={BASEMAPS[basemap].url}
          attribution={BASEMAPS[basemap].attribution}
          maxZoom={BASEMAPS[basemap].maxZoom}
        />
        {/* Heatmap layer — shown in heatmap mode */}
        {mapMode === 'heatmap' && heatPoints.length > 0 && (
          <HeatmapLayer
            points={heatPoints}
            radius={40}
            blur={30}
            max={0.5}
            minOpacity={0.45}
            gradient={{
              0.0:  '#0ea5e9',   // sky-blue  — even sparse areas visible
              0.3:  '#22d3ee',   // cyan
              0.55: '#facc15',   // yellow
              0.75: '#f97316',   // orange
              1.0:  '#ef4444',   // red       — dense clusters
            }}
          />
        )}

        {/* ── Protected Areas GeoJSON overlay (from shapefile) ── */}
        {mapLayers.parks && parksGeoJson && (
          <GeoJSON
            key={`protected-areas-${basemap}`}
            data={parksGeoJson}
            style={(feature) => {
              const cat      = feature?.properties?.IUCN_CAT ?? feature?.properties?.iucn_cat ?? '';
              const isStrict = ['Ia', 'Ib', 'II'].includes(cat);

              // Colour chosen to contrast against each basemap:
              //  dark      → bright green  (stands out on near-black tiles)
              //  street    → violet/purple (Google Street uses green for parks/vegetation)
              //  satellite → amber/gold    (contrasts against dark-green canopy)
              const color =
                basemap === 'dark'      ? '#4ade80' :
                basemap === 'street'    ? '#a855f7' :
                /* satellite */           '#fbbf24';

              return {
                color,
                fillColor:   color,
                fillOpacity: isStrict ? 0.18 : 0.09,
                weight:      basemap === 'dark' ? 1.5 : 2,
                dashArray:   '5 4',
                opacity:     0.85,
              };
            }}
            onEachFeature={(feature, layer) => {
              const p   = feature.properties ?? {};
              const name = p.NAME ?? p.name ?? p.NAME_E ?? p.WDPA_PID ?? 'Protected Area';
              const type = p.DESIG_ENG ?? p.desig_eng ?? p.DESIG ?? p.designation ?? '';
              const cat  = p.IUCN_CAT  ?? p.iucn_cat  ?? '';
              const area = p.GIS_AREA  ?? p.REP_AREA  ?? p.area ?? '';
              layer.bindPopup(`
                <div style="font-family:var(--font-body);font-size:12px;min-width:160px">
                  <p style="font-weight:700;margin-bottom:4px">${name}</p>
                  ${type ? `<p style="color:#6b7280;margin-bottom:2px">${type}</p>` : ''}
                  ${cat  ? `<p style="color:#6b7280;margin-bottom:2px">IUCN: ${cat}</p>` : ''}
                  ${area ? `<p style="color:#9ca3af;font-size:10px">${Number(area).toLocaleString()} km²</p>` : ''}
                </div>
              `);
            }}
          />
        )}

        {/* ── Migration corridors overlay ── */}
        {mapLayers.migration && MIGRATION_CORRIDORS.map((c) => (
          <Polyline
            key={c.name}
            positions={c.pts}
            pathOptions={{ color: '#60a5fa', weight: 2.5, opacity: 0.7, dashArray: '6 4' }}
          >
            <Popup>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 12 }}>
                <p style={{ fontWeight: 700 }}>{c.name}</p>
                <p style={{ color: '#6b7280', marginTop: 2 }}>Wildlife migration corridor</p>
              </div>
            </Popup>
          </Polyline>
        ))}

        {/* ── Trafficking routes overlay ── */}
        {mapLayers.routes && trafficRoutes.map((r, i) => (
          <Polyline
            key={i}
            positions={r.pts}
            pathOptions={{ color: '#f87171', weight: 2, opacity: 0.75 }}
          >
            <Popup>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 12 }}>
                <p style={{ fontWeight: 700, marginBottom: 2, textTransform: 'capitalize' }}>{r.name}</p>
                <p style={{ color: '#6b7280' }}>Trafficking route — {r.pts.length} linked locations</p>
              </div>
            </Popup>
          </Polyline>
        ))}

        {/* Individual markers — shown in markers mode */}
        {mapMode === 'markers' && data.map((itm: any, idx: number) =>
          itm?.latitude != null && itm?.longitude != null ? (
            <CircleMarker
              key={idx}
              center={[Number(itm.latitude), Number(itm.longitude)]}
              radius={7}
              color="rgba(255,255,255,0.45)"
              weight={1}
              fillColor={markerFill(itm.caseStatus)}
              fillOpacity={0.88}
            >
              <Popup>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, minWidth: 190, lineHeight: 1.7 }}>
                  <p style={{ fontWeight: 700, marginBottom: 3, fontSize: 13 }}>{itm.species}</p>
                  <p style={{ color: '#6b7280', marginBottom: 2 }}>{itm?.location}</p>
                  {itm?.nameOfAccused && <p style={{ color: '#6b7280' }}>Accused: {maskName(itm.nameOfAccused, role)}</p>}
                  <span style={{ display: 'inline-block', marginTop: 7, padding: '2px 10px', borderRadius: 999, fontSize: 10, fontWeight: 600, ...statusPopupStyle(itm.caseStatus) }}>
                    {itm.caseStatus}
                  </span>
                </div>
              </Popup>
            </CircleMarker>
          ) : null
        )}
      </MapContainer>

      {/* Filter bar — floating overlay */}
      <div style={{ position: 'absolute', top: 14, left: 14, right: 14, zIndex: 1001 }}>
        <div
          className="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-xl"
          style={{ background: 'rgba(4,31,14,0.88)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(14px)' }}
        >
          {/* Map mode toggle */}
          <div className="flex items-center rounded-lg overflow-hidden border border-white/10 shrink-0" style={{ background: 'rgba(0,0,0,0.25)' }}>
            {(['markers', 'heatmap'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMapMode(m)}
                className={`px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide transition-all ${
                  mapMode === m
                    ? 'bg-green-700/60 text-green-300'
                    : 'text-white/30 hover:text-white/55'
                }`}
              >
                {m === 'markers' ? '⬤ Pins' : '◉ Heat'}
              </button>
            ))}
          </div>
          <div className="w-px h-3.5 bg-white/10 shrink-0" />
          <span className="text-white/30 text-[10px] tracking-widest uppercase shrink-0 hidden sm:block">Filter</span>
          <div className="w-px h-3.5 bg-white/10 hidden sm:block" />
          {[
            { value: species, set: setSpecies, maxW: '7rem', opts: [['all', 'All Species'], ...speciesList.sort().map((s) => [s, s.charAt(0).toUpperCase() + s.slice(1)])] },
            { value: status,  set: setStatus,  maxW: undefined, opts: [['all','All Statuses'],['Concluded','Concluded'],['Ongoing','Ongoing'],['Absconded','Absconded']] },
            { value: month,   set: setMonth,   maxW: undefined, opts: [['all','All Months'], ...MONTHS.map((m) => [m, m])] },
            { value: year,    set: setYear,    maxW: undefined, opts: [['all','All Years'], ...Array.from({ length: 16 }, (_, i) => [(2010+i).toString(),(2010+i).toString()])] },
          ].map((f, i) => (
            <select key={i} className={selCls} style={f.maxW ? { maxWidth: f.maxW } : undefined} value={f.value} onChange={(e) => f.set(e.target.value)}>
              {f.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ))}
          <div className="w-px h-3.5 bg-white/10 shrink-0" />
          {/* Layer toggles */}
          {/* Parks toggle — shows spinner while loading, red tint on error */}
          <button
            onClick={() => setMapLayers((prev) => ({ ...prev, parks: !prev.parks }))}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all border flex items-center gap-1.5 ${
              parksError
                ? 'text-red-400 border-red-500/30 bg-red-500/10'
                : mapLayers.parks
                ? 'bg-green-700/40 text-green-300 border-green-500/30'
                : 'text-white/30 border-white/10 hover:text-white/55 hover:border-white/20'
            }`}
            title={parksError ? 'Failed to load protected areas shapefile' : undefined}
          >
            {parksLoading
              ? <span className="inline-block w-2.5 h-2.5 rounded-full border border-green-400/60 border-t-green-400 animate-spin" />
              : '🌿'}
            {parksError ? 'Parks ✕' : 'Parks'}
          </button>
          {[
            { key: 'routes',    label: '🔴 Routes',    active: mapLayers.routes },
            { key: 'migration', label: '🔵 Migration', active: mapLayers.migration },
          ].map(({ key, label, active }) => (
            <button
              key={key}
              onClick={() => setMapLayers((prev) => ({ ...prev, [key]: !prev[key as keyof typeof prev] }))}
              className={`px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all border ${
                active
                  ? 'bg-green-700/40 text-green-300 border-green-500/30'
                  : 'text-white/30 border-white/10 hover:text-white/55 hover:border-white/20'
              }`}
            >
              {label}
            </button>
          ))}

          <div className="w-px h-3.5 bg-white/10 shrink-0" />

          {/* Basemap switcher */}
          <div className="flex items-center rounded-lg overflow-hidden border border-white/10 shrink-0" style={{ background: 'rgba(0,0,0,0.25)' }}>
            {(['dark', 'street', 'satellite'] as BasemapKey[]).map((bm) => (
              <button
                key={bm}
                onClick={() => setBasemap(bm)}
                title={BASEMAPS[bm].label}
                className={`px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide transition-all ${
                  basemap === bm
                    ? 'bg-green-700/60 text-green-300'
                    : 'text-white/30 hover:text-white/55'
                }`}
              >
                {bm === 'dark' ? '🌑' : bm === 'street' ? '🗺' : '🛰'} {BASEMAPS[bm].label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stat chips */}
      <div style={{ position: 'absolute', bottom: 20, left: 14, zIndex: 1001 }}>
        <div
          className="flex items-center gap-px rounded-xl overflow-hidden"
          style={{ border: '1px solid rgba(255,255,255,0.07)', backdropFilter: 'blur(14px)' }}
        >
          {[
            { label: 'Total',     value: data.length,        color: '#fff' },
            { label: 'Ongoing',   value: statuses.ongoing,   color: 'rgb(167,139,250)' },
            { label: 'Concluded', value: statuses.concluded, color: 'rgb(134,239,172)' },
            { label: 'Absconded', value: statuses.absconded, color: 'rgb(251,191,36)' },
          ].map((s) => (
            <div key={s.label} className="px-4 py-2.5 flex flex-col items-center" style={{ background: 'rgba(4,31,14,0.88)' }}>
              <span className="text-base font-bold tabular-nums leading-none" style={{ color: s.color, fontFamily: 'var(--font-display)' }}>{s.value}</span>
              <span className="text-[9px] text-white/35 mt-0.5 tracking-wide">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Legend — markers mode */}
      {mapMode === 'markers' && (
        <div style={{ position: 'absolute', bottom: 20, right: 14, zIndex: 1001 }}>
          <div
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl"
            style={{ background: 'rgba(4,31,14,0.88)', border: '1px solid rgba(255,255,255,0.07)', backdropFilter: 'blur(14px)' }}
          >
            {[
              { label: 'Ongoing',   color: 'rgba(77,5,232,1)' },
              { label: 'Concluded', color: 'rgb(15,219,32)' },
              { label: 'Absconded', color: 'rgba(248,148,6,1)' },
            ].map((l) => (
              <span key={l.label} className="flex items-center gap-1.5 text-[10px] text-white/40">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: l.color }} />
                {l.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Legend — heatmap mode */}
      {mapMode === 'heatmap' && (
        <div style={{ position: 'absolute', bottom: 20, right: 14, zIndex: 1001 }}>
          <div
            className="flex flex-col gap-2 px-4 py-3 rounded-xl"
            style={{ background: 'rgba(4,31,14,0.92)', border: '1px solid rgba(255,255,255,0.07)', backdropFilter: 'blur(14px)' }}
          >
            <p className="text-white/35 text-[9px] uppercase tracking-widest">Incident Density</p>
            <div className="flex items-center gap-2">
              <div
                className="h-2.5 w-28 rounded-full"
                style={{ background: 'linear-gradient(to right, #0ea5e9, #22d3ee, #facc15, #f97316, #ef4444)' }}
              />
            </div>
            <div className="flex justify-between text-[9px] text-white/30">
              <span>Low</span>
              <span>High</span>
            </div>
            {dataLoading && (
              <p className="text-white/35 text-[9px] mt-1">Loading&hellip;</p>
            )}
            {!dataLoading && heatPoints.length === 0 && (
              <p className="text-amber-400/70 text-[9px] mt-1">No geocoded records in current filter</p>
            )}
            {!dataLoading && heatPoints.length > 0 && (
              <p className="text-white/25 text-[9px]">{heatPoints.length} plotted points</p>
            )}
          </div>
        </div>
      )}

      {/* Loading overlay — shown while data is being fetched (both modes) */}
      {dataLoading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1001 }}>
          <div
            className="flex flex-col items-center gap-3 px-8 py-5 rounded-2xl text-center"
            style={{ background: 'rgba(4,31,14,0.92)', border: '1px solid rgba(255,255,255,0.10)', backdropFilter: 'blur(16px)' }}
          >
            <div className="w-7 h-7 rounded-full border-2 border-green-500/30 border-t-green-400 animate-spin" />
            <p className="text-white/60 text-sm font-semibold">Loading incident data&hellip;</p>
          </div>
        </div>
      )}

      {/* No-coordinates notice (heatmap mode, data loaded but none geocoded) */}
      {!dataLoading && mapMode === 'heatmap' && heatPoints.length === 0 && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1001 }}>
          <div
            className="flex flex-col items-center gap-2 px-6 py-4 rounded-2xl text-center"
            style={{ background: 'rgba(4,31,14,0.92)', border: '1px solid rgba(255,255,255,0.10)', backdropFilter: 'blur(16px)' }}
          >
            <span className="text-2xl">📍</span>
            <p className="text-white/70 text-sm font-semibold">No location data</p>
            <p className="text-white/35 text-xs max-w-[200px]">
              Heatmap requires geocoded records. Switch to <strong className="text-white/55">Pins</strong> mode or upload records with GPS coordinates.
            </p>
          </div>
        </div>
      )}

      {/* No-data notice (markers mode, data loaded but filter returns nothing) */}
      {!dataLoading && mapMode === 'markers' && data.length === 0 && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1001 }}>
          <div
            className="flex flex-col items-center gap-2 px-6 py-4 rounded-2xl text-center"
            style={{ background: 'rgba(4,31,14,0.92)', border: '1px solid rgba(255,255,255,0.10)', backdropFilter: 'blur(16px)' }}
          >
            <span className="text-2xl">🔍</span>
            <p className="text-white/70 text-sm font-semibold">No matching records</p>
            <p className="text-white/35 text-xs max-w-[200px]">
              Try adjusting the filters above to see incidents on the map.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  /* ── Analytics view ──────────────────────────────────────────────── */
  const AnalyticsView = (
    <div
      className="flex-1 overflow-y-auto p-5"
      style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total Cases',     value: data.length,        color: 'text-white',       bg: '#0a3d1f', border: 'border-white/8' },
          { label: 'Species Tracked', value: speciesList.length,  color: 'text-green-400',   bg: '#0a3d1f', border: 'border-green-500/15' },
          { label: 'Ongoing',         value: statuses.ongoing,    color: 'text-violet-400',  bg: '#160a38', border: 'border-violet-500/15' },
          { label: 'Concluded',       value: statuses.concluded,  color: 'text-emerald-400', bg: '#062b12', border: 'border-emerald-500/15' },
        ].map((s) => (
          <div key={s.label} className={`rounded-xl px-4 py-4 border ${s.border} flex flex-col gap-2`} style={{ background: s.bg }}>
            <span className="text-white/40 text-xs">{s.label}</span>
            <span className={`text-3xl font-bold tabular-nums ${s.color}`} style={{ fontFamily: 'var(--font-display)' }}>{s.value}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          {
            title: 'Cases by Status',
            chart: <Bar options={chartOpts} data={{ labels: ['Ongoing','Concluded'], datasets: [{ label: 'Cases', data: [statuses.ongoing, statuses.concluded], backgroundColor: ['rgba(77,5,232,0.8)','rgba(15,219,32,0.8)'], borderColor: ['rgba(77,5,232,1)','rgba(15,219,32,1)'], borderWidth: 1 }] }} />,
          },
          {
            title: 'Top Species',
            chart: <Bar options={chartOpts} data={{ labels: speciesChartData.labels, datasets: [{ label: 'Incidents', data: speciesChartData.values, backgroundColor: CHART_COLORS, borderWidth: 1 }] }} />,
          },
          {
            title: 'Seizures per Year',
            chart: <Bar options={chartOpts} data={{ labels: yearChartData.labels, datasets: [{ label: 'Seizures', data: yearChartData.values, backgroundColor: 'rgba(34,197,94,0.7)', borderColor: 'rgba(34,197,94,1)', borderWidth: 1 }] }} />,
          },
          {
            title: 'Offender Nationalities',
            chart: <Bar options={chartOpts} data={{ labels: natChartData.labels, datasets: [{ label: 'Offenders', data: natChartData.values, backgroundColor: 'rgba(255,99,132,0.75)', borderColor: 'rgba(255,99,132,1)', borderWidth: 1 }] }} />,
          },
        ].map(({ title, chart }) => (
          <div key={title} className="rounded-xl p-5 border border-white/6 flex flex-col gap-3" style={{ background: '#0a2e18' }}>
            <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase">{title}</p>
            <div style={{ height: 220 }}>{chart}</div>
          </div>
        ))}
      </div>

      {/* ── Judicial Intelligence section ──────────────────────────── */}
      <div className="mt-5">
        <div className="flex items-center gap-3 mb-4">
          <p className="text-white/55 text-xs font-semibold tracking-[0.12em] uppercase">Judicial Intelligence</p>
          <div className="h-px flex-1 bg-white/6" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Conviction rate gauge */}
          <div className="rounded-xl p-5 border border-white/6 flex flex-col items-center justify-center gap-3" style={{ background: '#0a2e18' }}>
            <p className="text-white/40 text-[10px] uppercase tracking-wider">Conviction Rate</p>
            <div className="relative w-28 h-28">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
                <circle
                  cx="50" cy="50" r="42" fill="none"
                  stroke={convictionRate >= 50 ? 'rgba(52,211,153,0.85)' : 'rgba(251,191,36,0.85)'}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${(convictionRate / 100) * 263.9} 263.9`}
                  style={{ transition: 'stroke-dasharray 1s ease' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-2xl font-bold tabular-nums ${convictionRate >= 50 ? 'text-emerald-400' : 'text-amber-400'}`} style={{ fontFamily: 'var(--font-display)' }}>
                  {convictionRate}%
                </span>
                <span className="text-white/25 text-[9px]">Concluded</span>
              </div>
            </div>
            <div className="flex gap-3 text-[10px]">
              <span className="text-emerald-400">{statuses.concluded} concluded</span>
              <span className="text-white/25">/</span>
              <span className="text-white/40">{data.length} total</span>
            </div>
          </div>

          {/* Conviction rate by year */}
          <div className="rounded-xl p-5 border border-white/6 flex flex-col gap-3" style={{ background: '#0a2e18' }}>
            <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase">Conviction Rate by Year</p>
            <div style={{ height: 160 }}>
              {convictionByYearData.labels.length > 1 ? (
                <Line
                  options={{
                    ...chartOpts,
                    scales: {
                      ...chartOpts.scales,
                      y: { ...chartOpts.scales.y, max: 100, ticks: { ...chartOpts.scales.y.ticks, callback: (v: any) => `${v}%` } },
                    },
                  } as any}
                  data={{
                    labels: convictionByYearData.labels,
                    datasets: [{
                      label: 'Conv. Rate',
                      data: convictionByYearData.convRate,
                      borderColor: 'rgba(52,211,153,0.9)',
                      backgroundColor: 'rgba(52,211,153,0.1)',
                      fill: true,
                      tension: 0.4,
                      pointRadius: 3,
                      pointBackgroundColor: 'rgba(52,211,153,1)',
                    }],
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-white/20 text-xs">Not enough year data</div>
              )}
            </div>
          </div>

          {/* Top courts */}
          <div className="rounded-xl p-5 border border-white/6 flex flex-col gap-3" style={{ background: '#0a2e18' }}>
            <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase">Top Courts by Cases</p>
            {courtChartData.labels.length > 0 ? (
              <div style={{ height: 160 }}>
                <Bar
                  options={{ ...chartOpts, indexAxis: 'y' as const } as any}
                  data={{
                    labels: courtChartData.labels,
                    datasets: [{
                      label: 'Cases',
                      data: courtChartData.values,
                      backgroundColor: 'rgba(96,165,250,0.7)',
                      borderColor: 'rgba(96,165,250,1)',
                      borderWidth: 1,
                      borderRadius: 3,
                    }],
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center flex-1 text-white/20 text-xs">No court data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#061a0e', fontFamily: 'var(--font-body)' }}>

      {/* Mobile sidebar overlay */}
      {mobileSidebar && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setMobileSidebar(false)}
          onKeyDown={(e) => e.key === 'Escape' && setMobileSidebar(false)}
        />
      )}

      {/* Mobile sidebar drawer — hidden when printing */}
      <aside
        className={`print-hidden fixed inset-y-0 left-0 z-50 flex flex-col md:hidden transition-transform duration-300 ${mobileSidebar ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ width: 220, zIndex:99999, background: '#041f0e', borderRight: '1px solid rgba(255,255,255,0.06)' }}
      >
        <SidebarContent compact={false} />
      </aside>

      {/* Desktop sidebar — hidden when printing */}
      <aside
        className="print-hidden hidden md:flex flex-col shrink-0 overflow-hidden border-r border-white/6"
        style={{
          width: sidebarOpen ? 220 : 56,
          background: '#041f0e',
          transition: 'width 0.22s ease',
        }}
      >
        <SidebarContent compact={!sidebarOpen} />
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">

        {/* Top bar — hidden when printing */}
        <div
          className="print-hidden flex items-center justify-between px-5 py-2.5 border-b border-white/6 shrink-0"
          style={{ background: '#052e16' }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileSidebar(true)}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-all md:hidden"
            >
              <Bars3Icon className="w-4 h-4" />
            </button>
            <div>
              <p className="text-white/85 text-sm font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                {VIEW_TITLE[activeView]}
              </p>
              <p className="text-white/30 text-[10px] tracking-wide hidden sm:block">Wildlife Information &amp; Tracking System</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/30 text-xs hidden sm:block tabular-nums">
              {data.length} records
            </span>
            {(role === 'admin' || role === 'super-admin') && (
              <button
                onClick={() => setUploadDrawerOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-green-400 border border-green-500/25 hover:bg-green-500/10 transition-all"
              >
                <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Upload Dataset</span>
              </button>
            )}
            <a href="/" className="flex items-center gap-1.5 text-white/30 text-xs hover:text-white/60 transition-colors">
              <ArrowLeftIcon className="w-3 h-3" />
              <span className="hidden sm:inline">Public Site</span>
            </a>
          </div>
        </div>

        {/* View content */}
        {activeView === 'map'                   && MapView}
        {activeView === 'analytics'             && AnalyticsView}
        {activeView === 'my-reports'            && <MyReportsView />}
        {activeView === 'my-report'             && <UserReportView allData={allData} userName={state.userData?.name || state.userData?.username} />}
        {activeView === 'species-watch'         && <SpeciesWatchView allData={allData} />}
        {activeView === 'offender-intelligence' && <OffenderIntelligenceView allData={allData} />}
        {activeView === 'network-analysis'         && <NetworkAnalysisView allData={allData} />}
        {activeView === 'sentencing-intelligence'  && <SentencingIntelligenceView allData={allData} />}
        {activeView === 'hearing-delay'            && <HearingDelayView allData={allData} />}
        {activeView === 'dataset-management'    && <DatasetView />}
        {activeView === 'reported-incidents'    && <ReportedIncidentsView />}
        {activeView === 'case-precedents'       && <CasePrecedentsView canEdit={role === 'admin' || role === 'super-admin'} />}
        {activeView === 'articles'              && <ArticlesView />}
        {activeView === 'user-management'       && <UserManagementView />}
        {activeView === 'system-health'         && <SystemHealthView allData={allData} />}
        {activeView === 'export'                && <ExportView allData={allData} />}
        {activeView === 'api-keys'              && <ApiKeysView />}
        {activeView === 'settings'              && <ProfileSettingsView />}
      </div>

      {/* ── Upload Dataset Drawer (admin / super-admin only) ─────── */}
      <Drawer
        opened={uploadDrawerOpen}
        onClose={() => setUploadDrawerOpen(false)}
        position="right"
        title="Upload Dataset"
        padding="md"
        size="xl"
        withOverlay={false}
        zIndex={1100}
        styles={{
          drawer: {
            background: "#061a0e",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexDirection: "column",
          },
          header: {
            background: "#061a0e",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            paddingTop: 14,
            paddingBottom: 14,
          },
          title: {
            color: "rgba(255,255,255,0.85)",
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: "0.02em",
          },
          closeButton: { color: "rgba(255,255,255,0.35)" },
          body: { flex: 1, display: "flex", flexDirection: "column", overflow: "auto" },
        }}
      >
        <ExcelUploader />
      </Drawer>
    </div>
  );
}
